import { inject } from '@angular/core';
import { NonNullableFormBuilder, Validators } from '@angular/forms';

import { I18nService } from '@core/i18n-legacy';
import { MAX_FILE_SIZE_MB, QuestionService } from '@tellent-ask-hr/dynamic-forms-smart-ui';
import type {
  DefineFormData,
  DynamicFormConfig,
  RespondFormWrapperConfig,
  UnwrapSignal,
} from '@tellent-ask-hr/dynamic-forms-types';
import type { FileTemplateItem } from '@tellent-ask-hr/templates-types';

export class FileConfig implements DynamicFormConfig {
  private readonly formBuilder = inject(NonNullableFormBuilder);
  private readonly questionService = inject(QuestionService);
  private readonly i18n = inject(I18nService);

  public readonly kind: FileTemplateItem['type'] = 'file';
  public readonly defineConfig: DynamicFormConfig['defineConfig'] = {
    name: this.i18n.translate('tlc.ask_hr.form_template_create.configs.add_file'),
    icon: 'attach-file',
  };

  public readonly components = {
    define: () => import('@tellent-ask-hr/dynamic-forms-smart-ui').then(m => m.QuestionComponent),
    respond: () =>
      import('@tellent-ask-hr/dynamic-forms-smart-ui').then(m => m.RespondFileComponent),
    present: () =>
      import('@tellent-ask-hr/dynamic-forms-smart-ui').then(m => m.FilePresenterComponent),
  };

  public readonly defineForm = (
    data?: DefineFormData,
  ): UnwrapSignal<
    import('@tellent-ask-hr/dynamic-forms-smart-ui').QuestionComponent['defineForm']
  > => this.questionService.createQuestionForm(data);

  public readonly respondConfig = (
    data: RespondFormWrapperConfig,
  ): UnwrapSignal<
    import('@tellent-ask-hr/dynamic-forms-smart-ui').RespondFileComponent['respondConfig']
  > => data;

  public readonly respondForm = (config: {
    required: boolean;
  }): UnwrapSignal<
    import('@tellent-ask-hr/dynamic-forms-smart-ui').RespondFileComponent['respondForm']
  > =>
    this.formBuilder.group({
      value: this.formBuilder.control<File | null>(null, [
        ...(config.required ? [Validators.required] : []),
        (control): { [key: string]: any } | null => {
          const file = control.value;
          if (!file) return null;
          const maxSizeInBytes = MAX_FILE_SIZE_MB * 1024 * 1024;
          const isValid = file.size <= maxSizeInBytes;
          return isValid ? null : { maxFileSize: true };
        },
      ]),
    });

  public readonly presentConfig = (
    data: FileTemplateItem,
  ): UnwrapSignal<
    import('@tellent-ask-hr/dynamic-forms-smart-ui').FilePresenterComponent['config']
  > => ({
    heading: data.name,
    value: data.value,
  });
}
